import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Chip,
  Avatar,
  LinearProgress,
  Paper,
  useTheme,
  useMediaQuery,
  Badge,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  People,
  Assessment,
  Assignment,
  Gavel,
  TrendingUp,
  AccountBalanceWallet,
  Warning,
  CheckCircle,
  Cancel,
  Pause,
  Refresh
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import P2PPartnerManagement from './P2PPartnerManagement';
import P2PRateManagement from './P2PRateManagement';
import P2POrderManagement from './P2POrderManagement';
import P2PAnalytics from './P2PAnalytics';

const P2PAdminDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const userRole = useSelector((state) => state.user.currentRole);
  const isAuth = useSelector((state) => state.auth.isAuthenticated);
  
  const [activeTab, setActiveTab] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is authenticated and has admin role before making API calls
    console.log('Admin Dashboard - Auth status:', isAuth);
    console.log('Admin Dashboard - User role:', userRole);
    
    if (isAuth && userRole === 'admin') {
      fetchSummary();
    } else {
      console.warn('User not authenticated or not admin:', { isAuth, userRole });
      setLoading(false);
      setSummary({
        partners: {},
        orders: {},
        analytics: {}
      });
    }
  }, [isAuth, userRole]);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const { default: apiClient } = await import('../../request/http-common');
      
      // Fetch summary data from multiple endpoints with individual error handling
      const promises = [
        apiClient.get('/api/admin/p2p/partners?per_page=1').catch(err => ({ data: { summary: {} }, error: err })),
        apiClient.get('/api/admin/p2p/orders?per_page=1').catch(err => ({ data: { summary: {} }, error: err })),
        apiClient.get('/api/admin/p2p/analytics').catch(err => ({ data: { analytics: { overview: {} } }, error: err }))
      ];

      const [partnersRes, ordersRes, analyticsRes] = await Promise.all(promises);

      // Debug logging
      console.log('Dashboard API Responses:');
      console.log('Partners response:', partnersRes);
      console.log('Orders response:', ordersRes);
      console.log('Analytics response:', analyticsRes);

      // Check for errors and log them
      if (partnersRes.error) {
        console.error('Error fetching partners:', partnersRes.error);
        console.error('Partners error details:', partnersRes.error.response?.data);
      }
      if (ordersRes.error) {
        console.error('Error fetching orders:', ordersRes.error);
        console.error('Orders error details:', ordersRes.error.response?.data);
      }
      if (analyticsRes.error) {
        console.error('Error fetching analytics:', analyticsRes.error);
        console.error('Analytics error details:', analyticsRes.error.response?.data);
      }

      setSummary({
        partners: partnersRes.data?.summary || {},
        orders: ordersRes.data?.summary || {},
        analytics: analyticsRes.data?.analytics?.overview || {}
      });
    } catch (error) {
      console.error('Error fetching P2P admin summary:', error);
      // Set empty summary to prevent component crashes
      setSummary({
        partners: {},
        orders: {},
        analytics: {}
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getStatusColor = (status, count) => {
    if (count === 0) return 'default';
    
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
      case 'completed':
        return 'success';
      case 'rejected':
      case 'cancelled':
        return 'error';
      case 'disputed':
        return 'error';
      case 'suspended':
        return 'error';
      default:
        return 'primary';
    }
  };

  const summaryCards = summary ? [
    {
      title: 'Total Partners',
      value: summary.partners?.total_partners || 0,
      icon: People,
      color: theme.palette.primary.main,
      bgColor: theme.palette.primary.light + '20',
      subtitle: `${summary.partners?.verified_partners || 0} verified`,
    },
    {
      title: 'Pending Reviews',
      value: summary.partners?.pending_partners || 0,
      icon: Warning,
      color: theme.palette.warning.main,
      bgColor: theme.palette.warning.light + '20',
      subtitle: 'Awaiting approval',
    },
    {
      title: 'Active Orders',
      value: (summary.orders?.total_orders || 0) - (summary.orders?.completed_orders || 0) - (summary.orders?.cancelled_orders || 0),
      icon: Assignment,
      color: theme.palette.info.main,
      bgColor: theme.palette.info.light + '20',
      subtitle: `${summary.orders?.disputed_orders || 0} disputed`,
    },
    {
      title: 'Total Volume',
      value: `$${(summary.analytics?.total_volume || 0).toLocaleString()}`,
      icon: TrendingUp,
      color: theme.palette.success.main,
      bgColor: theme.palette.success.light + '20',
      subtitle: 'All time',
    },
    {
      title: 'Dispute Rate',
      value: `${((summary.analytics?.dispute_rate || 0)).toFixed(1)}%`,
      icon: Gavel,
      color: theme.palette.error.main,
      bgColor: theme.palette.error.light + '20',
      subtitle: 'Current period',
    },
    {
      title: 'Completion Rate',
      value: `${(summary.orders?.total_orders > 0 ? ((summary.orders?.completed_orders || 0) / summary.orders?.total_orders * 100) : 0).toFixed(1)}%`,
      icon: CheckCircle,
      color: theme.palette.success.main,
      bgColor: theme.palette.success.light + '20',
      subtitle: 'Success rate',
    },
  ] : [];

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  // Check admin access first
  if (!isAuth) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            Authentication Required
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please log in to access the P2P admin dashboard.
          </Typography>
        </Box>
      </Container>
    );
  }

  if (userRole !== 'admin') {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            Admin Access Required
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            You need admin privileges to access this dashboard. Current role: {userRole || 'none'}
          </Typography>
        </Box>
      </Container>
    );
  }

  // Safety check to prevent crashes if summary is still null
  if (!summary) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            Unable to load P2P admin data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please check your admin permissions or try refreshing the page.
          </Typography>
          <Button variant="contained" onClick={fetchSummary}>
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h3" fontWeight="bold" color="primary" gutterBottom>
              P2P Admin Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage P2P partners, rates, orders, and system oversight
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchSummary} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
          {summaryCards.map((card, index) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  sx={{
                    height: isMobile ? '140px' : '160px',
                    minHeight: isMobile ? '140px' : '160px',
                    background: `linear-gradient(135deg, ${card.bgColor} 0%, ${card.color}10 100%)`,
                    border: `1px solid ${card.color}30`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 25px ${card.color}30`,
                    },
                  }}
                >
                  <CardContent sx={{ 
                    p: isMobile ? 1.5 : 2, 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'space-between' 
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: isMobile ? 1 : 1.5 }}>
                      <Avatar
                        sx={{
                          bgcolor: card.color,
                          width: isMobile ? 32 : 40,
                          height: isMobile ? 32 : 40,
                          mr: isMobile ? 1 : 2,
                        }}
                      >
                        <card.icon sx={{ fontSize: isMobile ? 16 : 20 }} />
                      </Avatar>
                    </Box>
                    <Typography
                      variant={isMobile ? "h6" : "h5"}
                      fontWeight="bold"
                      color={card.color}
                      gutterBottom
                      sx={{ lineHeight: 1.1 }}
                    >
                      {card.value}
                    </Typography>
                    <Typography
                      variant={isMobile ? "caption" : "body2"}
                      color="text.secondary"
                      fontWeight="medium"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem', lineHeight: 1.2 }}
                    >
                      {card.title}
                    </Typography>
                    {card.subtitle && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ 
                          display: 'block', 
                          mt: 0.5, 
                          fontSize: isMobile ? '0.6rem' : '0.75rem',
                          lineHeight: 1.1 
                        }}
                      >
                        {card.subtitle}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Main Content */}
        <Paper sx={{ width: '100%', bgcolor: 'background.paper' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <People />
                    <span>Partners</span>
                    {summary?.partners?.pending_partners > 0 && (
                      <Badge badgeContent={summary.partners.pending_partners} color="warning" />
                    )}
                  </Box>
                }
                sx={{ minHeight: 64 }}
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalanceWallet />
                    <span>Rates</span>
                  </Box>
                }
                sx={{ minHeight: 64 }}
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assignment />
                    <span>Orders</span>
                    {summary?.orders?.disputed_orders > 0 && (
                      <Badge badgeContent={summary.orders.disputed_orders} color="error" />
                    )}
                  </Box>
                }
                sx={{ minHeight: 64 }}
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assessment />
                    <span>Analytics</span>
                  </Box>
                }
                sx={{ minHeight: 64 }}
              />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            {activeTab === 0 && (
              <motion.div
                key="partners"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <P2PPartnerManagement onUpdate={fetchSummary} />
              </motion.div>
            )}

            {activeTab === 1 && (
              <motion.div
                key="rates"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <P2PRateManagement onUpdate={fetchSummary} />
              </motion.div>
            )}

            {activeTab === 2 && (
              <motion.div
                key="orders"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <P2POrderManagement onUpdate={fetchSummary} />
              </motion.div>
            )}

            {activeTab === 3 && (
              <motion.div
                key="analytics"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <P2PAnalytics />
              </motion.div>
            )}
          </Box>
        </Paper>
      </motion.div>
    </Container>
  );
};

export default P2PAdminDashboard;
